import { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Config } from "~/constants/configuration_provider";
import { roles } from "~/lib/iam/roles";
import { PLATFORM_POLICY_MRN } from "~/lib/mrn";
import {
  useGetOrganizationScopeQuery,
  useGetRegionScopeQuery,
  useGetSpaceScopeQuery,
  useGetWorkspaceScopeQuery,
} from "~/operations";
import { useRegions } from "~/providers/RegionsProvider";
import { ScopeContext } from "~/providers/ScopeProvider";

const roleActions = Array.from(
  new Set(roles.flatMap(({ allowedActions }) => allowedActions)),
);

const isPrivateInstance = Config.VITE_PRIVATE_INSTANCE === "true";

export enum ScopeType {
  Region = "region",
  Organization = "organization",
  Space = "space",
  Workspace = "workspace",
}

export type Scope = {
  type: ScopeType;
  id: string;
  mrn: string;
  name: string;
  description: string;
  params: URLSearchParams;
  iamActions: string[];
};

export type RegionScope = Scope & { type: ScopeType.Region };
export type OrganizationScope = Scope & { type: ScopeType.Organization };
export type SpaceScope = Scope & { type: ScopeType.Space };
export type WorkspaceScope = Scope & { type: ScopeType.Workspace };
export type ActiveScope =
  | RegionScope
  | OrganizationScope
  | SpaceScope
  | WorkspaceScope;

// Useful when a component wants to enforce it can only be used within a Space or Workspace
// `export type ComponentProps = { activeScope: SpaceOrWorkspaceScope }`
export type SpaceOrWorkspaceScope = SpaceScope | WorkspaceScope;
export function isSpaceOrWorkspaceScope(
  scope?: Scope,
): scope is SpaceOrWorkspaceScope {
  return scope?.type === ScopeType.Space || scope?.type === ScopeType.Workspace;
}

export const sharedSpacesOrgId = "shared-spaces";

export type SpaceId = string;
export type OrgId = string;
export type SpaceMrn = string;
export type OrgMrn = string;
export type WorkspaceMrn = string;
export type WorkspaceId = string;

export function organizationMrnFromId(id: OrgId): OrgMrn {
  return `//captain.api.mondoo.app/organizations/${id}`;
}

export function organizationIdFromMrn(mrn: OrgMrn): SpaceId {
  return mrn.split("/").pop()!;
}

export function spaceMrnFromId(id: SpaceId): SpaceMrn {
  return `//captain.api.mondoo.app/spaces/${id}`;
}

export function spaceIdFromMrn(mrn: SpaceMrn): SpaceId {
  return mrn.split("/").pop()!;
}

export type RegionResult = ReturnType<typeof useGetRegionScopeQuery>;
export type OrganizationResult = ReturnType<
  typeof useGetOrganizationScopeQuery
>;
export type SpaceResult = ReturnType<typeof useGetSpaceScopeQuery>;
export type WorkspaceResult = ReturnType<typeof useGetWorkspaceScopeQuery>;
export type ActiveResult = OrganizationResult | SpaceResult | WorkspaceResult;

// This isn't supported. Don't try to convert workspace id to mrn.
// Workspace mrn structure is likely to change in future.
// export function workspaceMrnFromId(id: WorkspaceId): WorkspaceMrn {
//   throw new Error("workspaceMrn can not safely be inferred from workspaceId");
// }

// Only used for convenient display purposes
export function workspaceIdFromMrn(mrn: WorkspaceMrn): WorkspaceId {
  return mrn.split("/").pop()!;
}

export type UseScopeContext = {
  regionResult?: RegionResult;
  regionScope?: RegionScope;
  organizationResult?: OrganizationResult;
  organizationScope?: OrganizationScope;
  spaceResult?: SpaceResult;
  spaceScope?: SpaceScope;
  workspaceResult?: WorkspaceResult;
  workspaceScope?: WorkspaceScope;
  activeResult?: ActiveResult;
  activeScope?: ActiveScope;
};

export type UseScopeParams = {
  regionId?: string;
  organizationId?: string;
  spaceId?: string;
  workspaceMrn?: string;
};

export function useScopeContext(params?: UseScopeParams): UseScopeContext {
  const [searchParams] = useSearchParams();
  const { selectedRegion } = useRegions();

  const workspaceIdParam =
    params?.workspaceMrn ?? searchParams.get("workspaceId");
  const workspaceMrn = workspaceIdParam || undefined;

  const workspaceResult = useGetWorkspaceScopeQuery({
    variables: { mrn: workspaceMrn || "skip", actions: roleActions },
    skip: !workspaceMrn,
  });
  const workspaceData =
    workspaceResult.data?.workspace.__typename === "Workspace"
      ? workspaceResult.data.workspace
      : undefined;
  const workspaceIamActions = workspaceResult.data?.iamActions || [];

  const spaceIdParam = params?.spaceId ?? searchParams.get("spaceId");
  const spaceId = spaceIdParam;
  const spaceMrn = spaceId ? spaceMrnFromId(spaceId) : undefined;
  const spaceResult = useGetSpaceScopeQuery({
    variables: { mrn: spaceMrn || "skip", actions: roleActions },
    skip: !spaceMrn,
  });
  const spaceData = spaceResult.data?.space;
  const spaceIamActions = spaceResult.data?.iamActions || [];

  const organizationIdParam =
    params?.organizationId ?? searchParams.get("organizationId");
  const organizationId = organizationIdParam || spaceData?.organization?.id;
  const organizationMrn = organizationId
    ? organizationMrnFromId(organizationId)
    : undefined;
  const organizationResult = useGetOrganizationScopeQuery({
    variables: {
      mrn: organizationMrn || "skip",
      actions: roleActions,
    },
    skip: !organizationMrn,
  });
  const organizationData = spaceData?.shared
    ? {
        id: sharedSpacesOrgId,
        mrn: organizationMrnFromId(sharedSpacesOrgId),
        name: "Shared Spaces",
        description:
          "Spaces that have been shared with you from other organizations.",
      }
    : organizationResult.data?.organization;
  const organizationIamActions = organizationResult.data?.iamActions || [];

  const regionName = params?.regionId ?? selectedRegion.name;
  const regionResult = useGetRegionScopeQuery({
    variables: {
      mrn: PLATFORM_POLICY_MRN,
      actions: roleActions,
    },
    skip: !isPrivateInstance,
  });
  const regionData = useMemo(
    () => ({
      id: regionName,
      mrn: PLATFORM_POLICY_MRN,
      name: regionName,
      description: `${regionName} region`,
    }),
    [regionName],
  );
  // Region permissions boundary is scoped to platform when platformPolicy is enabled.
  // Otherwise, UI assumes the user has permission to all actions scoped to platform.
  const regionIamActions = !isPrivateInstance
    ? roleActions
    : regionResult.data?.iamActions || [];

  // Workspace:    some/path?spaceId=space&workspaceId=workspace
  // Space:        some/path?spaceId=space
  // Organization: some/path?organizationId=org
  // Region:       some/path
  const workspaceParams = useMemo(
    () =>
      new URLSearchParams(
        spaceId && workspaceMrn
          ? {
              spaceId,
              workspaceId: workspaceMrn,
            }
          : {},
      ),
    [spaceId, workspaceMrn],
  );
  const spaceParams = useMemo(
    () => new URLSearchParams(spaceId ? { spaceId } : {}),
    [spaceId],
  );
  const organizationParams = useMemo(
    () => new URLSearchParams(organizationId ? { organizationId } : {}),
    [organizationId],
  );
  const regionParams = useMemo(() => new URLSearchParams(), []);

  const regionScope: RegionScope | undefined = useMemo(
    () => ({
      type: ScopeType.Region,
      id: regionData.id,
      mrn: regionData.mrn,
      name: regionData.name,
      description: regionData.description,
      iamActions: regionIamActions,
      params: regionParams,
    }),
    [regionData, regionIamActions, regionParams],
  );

  const organizationScope: OrganizationScope | undefined = useMemo(
    () =>
      organizationData
        ? {
            type: ScopeType.Organization,
            id: organizationData.id,
            mrn: organizationData.mrn,
            name: organizationData.name,
            description: organizationData.description,
            iamActions: organizationIamActions,
            params: organizationParams,
          }
        : undefined,
    [organizationData, organizationIamActions, organizationParams],
  );

  const spaceScope: SpaceScope | undefined = useMemo(
    () =>
      spaceData
        ? {
            type: ScopeType.Space,
            id: spaceData.id,
            mrn: spaceData.mrn,
            name: spaceData.name,
            description: spaceData.description,
            iamActions: spaceIamActions,
            params: spaceParams,
          }
        : undefined,
    [spaceData, spaceIamActions, spaceParams],
  );

  const workspaceScope: WorkspaceScope | undefined = useMemo(
    () =>
      workspaceData
        ? {
            type: ScopeType.Workspace,
            id: workspaceIdFromMrn(workspaceData.mrn),
            mrn: workspaceData.mrn,
            name: workspaceData.name,
            description: workspaceData.description || "",
            iamActions: workspaceIamActions,
            params: workspaceParams,
          }
        : undefined,
    [workspaceData, workspaceIamActions, workspaceParams],
  );

  const activeScope =
    workspaceIdParam && spaceIdParam
      ? workspaceScope
      : spaceIdParam
        ? spaceScope
        : organizationIdParam
          ? organizationScope
          : regionScope;

  const activeResult =
    workspaceIdParam && spaceIdParam
      ? workspaceResult
      : spaceIdParam
        ? spaceResult
        : organizationIdParam
          ? organizationResult
          : undefined;

  return {
    regionResult,
    regionScope,
    organizationResult,
    organizationScope,
    spaceResult,
    spaceScope,
    workspaceResult,
    workspaceScope,
    activeResult,
    activeScope,
  };
}

export function useScope() {
  const context = useContext(ScopeContext);
  if (context === undefined) {
    throw new Error("useScope must be used within a ScopeProvider");
  }
  return context;
}
